import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Building2, Users, Mail, UserPlus, Trash2, Copy, Check, Settings, Crown, Upload, FileSpreadsheet, X } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InstitutionDashboard() {
  const { user } = useAuth();
  const [institution, setInstitution] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showCreateInstitution, setShowCreateInstitution] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', name: '', role: 'student' });
  const [newInstitution, setNewInstitution] = useState({ name: '', domains: '' });
  const [copiedCode, setCopiedCode] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchInstitution();
  }, []);

  const fetchInstitution = async () => {
    try {
      const response = await axios.get(`${API}/institutions/my`);
      if (response.data) {
        setInstitution(response.data);
        fetchMembers(response.data.id);
      }
    } catch (error) {
      console.error('Failed to load institution');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (institutionId) => {
    try {
      const response = await axios.get(`${API}/institutions/${institutionId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load members');
    }
  };

  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    try {
      const domains = newInstitution.domains.split(',').map(d => d.trim()).filter(d => d);
      const response = await axios.post(`${API}/institutions`, {
        name: newInstitution.name,
        domains: domains
      });
      toast.success('Institution created successfully!');
      setInstitution(response.data.institution);
      setShowCreateInstitution(false);
      fetchMembers(response.data.institution.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create institution');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/institutions/${institution.id}/members`, newMember);
      toast.success('Member added successfully!');
      setShowAddMember(false);
      setNewMember({ email: '', name: '', role: 'student' });
      fetchMembers(institution.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName}?`)) return;
    
    try {
      await axios.delete(`${API}/institutions/${institution.id}/members/${memberId}`);
      toast.success('Member removed successfully');
      fetchMembers(institution.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files[0];
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setBulkImporting(true);
    setBulkImportResults(null);

    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('default_role', 'student');

      const response = await axios.post(
        `${API}/institutions/${institution.id}/members/bulk`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setBulkImportResults(response.data.results);
      toast.success(response.data.message);
      fetchMembers(institution.id);
      
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import members');
    } finally {
      setBulkImporting(false);
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = "email,name,role\njohn.doe@example.com,John Doe,student\njane.smith@example.com,Jane Smith,instructor\nstudent@university.edu,Student Name,student";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'members_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(institution.invite_code);
    setCopiedCode(true);
    toast.success('Invite code copied!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'institution_admin': return 'bg-purple-100 text-purple-700';
      case 'instructor': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getMembershipBadgeColor = (type) => {
    switch (type) {
      case 'auto_domain': return 'bg-green-100 text-green-700';
      case 'invite_code': return 'bg-orange-100 text-orange-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  // No institution - show create option
  if (!institution) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-12 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 rounded border border-slate-200 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h1 className="text-3xl font-serif font-bold mb-4">No Institution Found</h1>
              <p className="text-slate-600 mb-6">
                You are not part of any institution. Create one to manage your organization's access.
              </p>
              
              {!showCreateInstitution ? (
                <button
                  onClick={() => setShowCreateInstitution(true)}
                  className="px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all"
                  data-testid="create-institution-btn"
                >
                  Create Institution
                </button>
              ) : (
                <form onSubmit={handleCreateInstitution} className="text-left space-y-4 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Institution Name *</label>
                    <input
                      type="text"
                      required
                      value={newInstitution.name}
                      onChange={(e) => setNewInstitution({...newInstitution, name: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                      placeholder="e.g., Harvard Business School"
                      data-testid="institution-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Domains (comma-separated)</label>
                    <input
                      type="text"
                      value={newInstitution.domains}
                      onChange={(e) => setNewInstitution({...newInstitution, domains: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                      placeholder="e.g., harvard.edu, hbs.edu"
                      data-testid="institution-domains-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Users with these email domains will be auto-verified</p>
                  </div>
                  <div className="flex gap-4">
                    <button type="submit" className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">
                      Create Institution
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateInstitution(false)}
                      className="px-6 py-2 border border-slate-300 rounded hover:border-slate-900"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Institution Header */}
          <div className="bg-white p-6 rounded border border-slate-200 mb-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-slate-900 rounded flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-serif font-bold text-slate-900" data-testid="institution-name">
                    {institution.name}
                  </h1>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {members.length} members
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      institution.subscription_status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {institution.subscription_status === 'active' ? 'Active Subscription' : 'Trial'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-sm text-slate-600 mb-1">Invite Code</div>
                <div className="flex items-center gap-2">
                  <code className="px-3 py-2 bg-slate-100 rounded font-mono text-lg" data-testid="invite-code">
                    {institution.invite_code}
                  </code>
                  <button
                    onClick={copyInviteCode}
                    className="p-2 hover:bg-slate-100 rounded transition-colors"
                    title="Copy invite code"
                  >
                    {copiedCode ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-slate-600" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Share this code with members to join</p>
              </div>
            </div>
            
            {institution.domains && institution.domains.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="text-sm text-slate-600">
                  <strong>Auto-verified domains:</strong>{' '}
                  {institution.domains.map((d, i) => (
                    <span key={d} className="inline-block px-2 py-1 bg-green-50 text-green-700 rounded text-xs mr-2">
                      @{d}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-4 border-b border-slate-200 mb-6">
            <button
              onClick={() => setActiveTab('members')}
              className={`pb-4 border-b-2 transition-colors ${
                activeTab === 'members' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
              }`}
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`pb-4 border-b-2 transition-colors ${
                activeTab === 'settings' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
              }`}
            >
              Settings
            </button>
          </div>

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div>
              <div className="mb-6 flex gap-4">
                <button
                  onClick={() => setShowAddMember(true)}
                  className="px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all flex items-center gap-2"
                  data-testid="add-member-btn"
                >
                  <UserPlus className="w-5 h-5" />
                  Add Member
                </button>
                <button
                  onClick={() => setShowBulkImport(true)}
                  className="px-6 py-3 bg-slate-900 text-white rounded hover:bg-slate-800 transition-all flex items-center gap-2"
                  data-testid="bulk-import-btn"
                >
                  <Upload className="w-5 h-5" />
                  Bulk Import CSV
                </button>
              </div>

              {/* Bulk Import Section */}
              {showBulkImport && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6" data-testid="bulk-import-form">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Bulk Import Members</h3>
                    <button
                      onClick={() => {
                        setShowBulkImport(false);
                        setBulkImportResults(null);
                      }}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="bg-slate-50 p-4 rounded border border-slate-200 mb-4">
                    <div className="flex items-start gap-3">
                      <FileSpreadsheet className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div className="text-sm text-slate-600">
                        <p className="font-medium mb-1">CSV Format Requirements:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Include columns: <code className="bg-slate-200 px-1 rounded">email</code>, <code className="bg-slate-200 px-1 rounded">name</code> (optional), <code className="bg-slate-200 px-1 rounded">role</code> (optional)</li>
                          <li>Role can be "student" or "instructor" (defaults to student)</li>
                          <li>Existing members will be skipped</li>
                        </ul>
                        <button
                          onClick={downloadSampleCsv}
                          className="mt-2 text-orange-600 hover:text-orange-700 underline"
                        >
                          Download sample template
                        </button>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleBulkImport} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Select CSV File *</label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv"
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer"
                        data-testid="csv-file-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={bulkImporting}
                      className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                      data-testid="import-csv-btn"
                    >
                      {bulkImporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Import Members
                        </>
                      )}
                    </button>
                  </form>

                  {/* Import Results */}
                  {bulkImportResults && (
                    <div className="mt-6 space-y-4">
                      <h4 className="font-semibold">Import Results</h4>
                      
                      {bulkImportResults.added?.length > 0 && (
                        <div className="bg-green-50 p-4 rounded border border-green-200">
                          <p className="font-medium text-green-700 mb-2">Added ({bulkImportResults.added.length})</p>
                          <div className="text-sm text-green-600 max-h-32 overflow-y-auto">
                            {bulkImportResults.added.map((m, i) => (
                              <div key={i}>{m.email} - {m.name || 'No name'} ({m.role})</div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {bulkImportResults.invited?.length > 0 && (
                        <div className="bg-blue-50 p-4 rounded border border-blue-200">
                          <p className="font-medium text-blue-700 mb-2">Invited - Pending Registration ({bulkImportResults.invited.length})</p>
                          <div className="text-sm text-blue-600 max-h-32 overflow-y-auto">
                            {bulkImportResults.invited.map((m, i) => (
                              <div key={i}>{m.email} - {m.name || 'No name'} ({m.role})</div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {bulkImportResults.skipped?.length > 0 && (
                        <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                          <p className="font-medium text-yellow-700 mb-2">Skipped ({bulkImportResults.skipped.length})</p>
                          <div className="text-sm text-yellow-600 max-h-32 overflow-y-auto">
                            {bulkImportResults.skipped.map((m, i) => (
                              <div key={i}>{m.email} - {m.reason}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {bulkImportResults.errors?.length > 0 && (
                        <div className="bg-red-50 p-4 rounded border border-red-200">
                          <p className="font-medium text-red-700 mb-2">Errors ({bulkImportResults.errors.length})</p>
                          <div className="text-sm text-red-600 max-h-32 overflow-y-auto">
                            {bulkImportResults.errors.map((e, i) => (
                              <div key={i}>Row {e.row}: {e.email || ''} - {e.error}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showAddMember && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6" data-testid="add-member-form">
                  <h3 className="text-xl font-semibold mb-4">Add New Member</h3>
                  <form onSubmit={handleAddMember} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          required
                          value={newMember.email}
                          onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          placeholder="user@example.com"
                          data-testid="member-email-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Name (Optional)</label>
                        <input
                          type="text"
                          value={newMember.name}
                          onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          placeholder="John Doe"
                          data-testid="member-name-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                      <select
                        value={newMember.role}
                        onChange={(e) => setNewMember({...newMember, role: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        data-testid="member-role-select"
                      >
                        <option value="student">Student</option>
                        <option value="instructor">Instructor</option>
                      </select>
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">
                        Add Member
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddMember(false);
                          setNewMember({ email: '', name: '', role: 'student' });
                        }}
                        className="px-6 py-2 border border-slate-300 rounded hover:border-slate-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Members Table */}
              <div className="bg-white rounded border border-slate-200">
                <table className="w-full">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold">Member</th>
                      <th className="text-left p-4 font-semibold">Email</th>
                      <th className="text-left p-4 font-semibold">Role</th>
                      <th className="text-left p-4 font-semibold">Join Method</th>
                      <th className="text-left p-4 font-semibold">Status</th>
                      <th className="text-left p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          No members yet. Add members using the button above or share the invite code.
                        </td>
                      </tr>
                    ) : (
                      members.map((member) => (
                        <tr key={member.id} className="border-b border-slate-200">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {member.role === 'institution_admin' && (
                                <Crown className="w-4 h-4 text-purple-600" />
                              )}
                              {member.user_name || 'Pending'}
                            </div>
                          </td>
                          <td className="p-4 text-slate-600">{member.user_email}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs capitalize ${getRoleBadgeColor(member.role)}`}>
                              {member.role.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${getMembershipBadgeColor(member.membership_type)}`}>
                              {member.membership_type === 'auto_domain' ? 'Domain Verified' : 
                               member.membership_type === 'invite_code' ? 'Invite Code' : 'Manual'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {member.status}
                            </span>
                          </td>
                          <td className="p-4">
                            {member.role !== 'institution_admin' && (
                              <button
                                onClick={() => handleRemoveMember(member.id, member.user_name || member.user_email)}
                                className="text-red-600 hover:text-red-700 flex items-center gap-1 text-sm"
                                data-testid={`remove-member-${member.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white p-6 rounded border border-slate-200">
              <h3 className="text-xl font-semibold mb-4">Institution Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Institution Name</label>
                  <input
                    type="text"
                    value={institution.name}
                    readOnly
                    className="w-full px-4 py-2 border border-slate-300 rounded bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Auto-Verified Domains</label>
                  <input
                    type="text"
                    value={institution.domains?.join(', ') || ''}
                    readOnly
                    className="w-full px-4 py-2 border border-slate-300 rounded bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subscription Status</label>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-2 rounded ${
                      institution.subscription_status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {institution.subscription_status === 'active' ? 'Active' : 'Trial'}
                    </span>
                    {institution.subscription_end && (
                      <span className="text-slate-600">
                        Expires: {new Date(institution.subscription_end).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
